// Freefall detection from devicemotion samples.
//
// `event.acceleration` excludes gravity, so it reads ~0 at rest and
// ~g while falling. A rolling window of samples must all read as
// freefall before we trigger, to stop sporadic screaming.

import { FREEFALL_LOWER, FREEFALL_UPPER, FREEFALL_WINDOW_MS } from './constants.js'

const recentSamples: boolean[] = []

/*
 * Magnitude of the acceleration vector, or undefined when the
 * device reports no data for an axis.
 */
export const readMagnitude = (acceleration: DeviceMotionEventAcceleration): number | undefined => {
  const { x, y, z } = acceleration

  if (x === null || y === null || z === null) {
    return undefined
  }

  return Math.hypot(x, y, z)
}

/*
 * How many consecutive samples cover the freefall window, given the
 * sensor's reporting interval in milliseconds.
 */
export const countRequiredSamples = (intervalMs: number): number => {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    return 2
  }

  return Math.max(2, Math.round(FREEFALL_WINDOW_MS / intervalMs))
}

/*
 * Record one devicemotion sample; return true when the whole window
 * indicates freefall.
 */
export const recordSample = (event: DeviceMotionEvent): boolean => {
  const acceleration = event.acceleration
  if (!acceleration) {
    return false
  }

  const magnitude = readMagnitude(acceleration)
  if (magnitude === undefined) {
    return false
  }

  const isFreefall = magnitude > FREEFALL_LOWER && magnitude < FREEFALL_UPPER
  const required = countRequiredSamples(event.interval)

  recentSamples.push(isFreefall)
  while (recentSamples.length > required) {
    recentSamples.shift()
  }

  return recentSamples.length >= required && recentSamples.every(sample => sample)
}
